import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Chip, Alert, CircularProgress
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

// Use axios with auth headers from localStorage
const api = {
  get:    (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  post:   (url, data, cfg={}) => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  put:    (url, data, cfg={}) => axios.put(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  delete: (url, cfg={}) => axios.delete(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
};

const GRADE_TABLE = [
  { min:90, letter:'A+', points:4.0 }, { min:85, letter:'A',  points:4.0 },
  { min:80, letter:'A-', points:3.7 }, { min:75, letter:'B+', points:3.3 },
  { min:70, letter:'B',  points:3.0 }, { min:65, letter:'B-', points:2.7 },
  { min:60, letter:'C+', points:2.3 }, { min:55, letter:'C',  points:2.0 },
  { min:50, letter:'C-', points:1.7 }, { min:45, letter:'D',  points:1.0 },
  { min:0,  letter:'F',  points:0.0 },
];

function calcGrade(ca, exam) {
  const total = parseFloat(ca||0)*0.4 + parseFloat(exam||0)*0.6;
  const g = GRADE_TABLE.find(g => total >= g.min) || GRADE_TABLE[GRADE_TABLE.length-1];
  return { total: total.toFixed(1), ...g };
}

function gradeColor(letter) {
  if (!letter) return 'default';
  if (['A+','A','A-'].includes(letter)) return 'success';
  if (['B+','B','B-'].includes(letter)) return 'primary';
  if (['C+','C','C-'].includes(letter)) return 'warning';
  if (letter === 'D') return 'warning';
  return 'error';
}

export default function GPAScoresheet() {
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses]     = useState([]);
  const [years, setYears]         = useState([]);
  const [selectedCourse, setCourse] = useState('');
  const [selectedYear, setYear]   = useState('');
  const [semester, setSemester]   = useState(1);
  const [rows, setRows]           = useState([]);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    api.get('/tertiary/courses').then(r => setCourses(r.data.data || []));
    api.get('/academic-years').then(r => setYears(r.data.data || []));
  }, []);

  const load = async () => {
    if (!selectedCourse || !selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/tertiary/course-scoresheet', {
        params: { course_id: selectedCourse, academic_year_id: selectedYear, semester }
      });
      setRows((r.data.data || []).map(s => ({
        ...s,
        ca:   s.continuous_assessment || '',
        exam: s.final_exam || '',
      })));
    } catch { enqueueSnackbar('Failed to load scoresheet', { variant:'error' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selectedCourse, selectedYear, semester]);

  const updateRow = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const save = async () => {
    setSaving(true);
    try {
      const grades = rows.map(r => ({
        registration_id: r.registration_id,
        student_id:      r.student_id,
        course_id:       selectedCourse,
        academic_year_id: selectedYear,
        semester,
        continuous_assessment: r.ca,
        final_exam:            r.exam,
      }));
      await api.post('/tertiary/grades', { grades });
      enqueueSnackbar(`Grades saved for ${grades.length} students`, { variant:'success' });
      load();
    } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Save failed', { variant:'error' }); }
    finally { setSaving(false); }
  };

  const course = courses.find(c => String(c.id) === String(selectedCourse));

  // Summary stats
  const graded = rows.filter(r => r.ca !== '' || r.exam !== '');
  const avgTotal = graded.length
    ? (graded.reduce((s, r) => s + parseFloat(calcGrade(r.ca, r.exam).total), 0) / graded.length).toFixed(1)
    : '—';
  const passCount = graded.filter(r => parseFloat(calcGrade(r.ca, r.exam).total) >= 50).length;

  return (
    <Box sx={{ p:3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>GPA Scoresheet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb:3 }}>
        Enter continuous assessment (CA) and final exam marks. GPA is calculated automatically.
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb:3 }} flexWrap="wrap">
        <TextField select size="small" label="Course" value={selectedCourse}
          onChange={e => setCourse(e.target.value)} sx={{ minWidth:280 }}>
          <MenuItem value="">Select Course</MenuItem>
          {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Academic Year" value={selectedYear}
          onChange={e => setYear(e.target.value)} sx={{ minWidth:160 }}>
          <MenuItem value="">Select Year</MenuItem>
          {years.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Semester" value={semester}
          onChange={e => setSemester(e.target.value)} sx={{ minWidth:130 }}>
          <MenuItem value={1}>Semester 1</MenuItem>
          <MenuItem value={2}>Semester 2</MenuItem>
        </TextField>
      </Stack>

      {/* Course info */}
      {course && (
        <Alert severity="info" sx={{ mb:2 }}>
          <strong>{course.code} — {course.name}</strong> &nbsp;|&nbsp;
          Credits: {course.credits} &nbsp;|&nbsp;
          CA weight: 40% &nbsp;|&nbsp; Exam weight: 60%
          {graded.length > 0 && <>&nbsp;|&nbsp; Class Average: <strong>{avgTotal}%</strong> &nbsp;|&nbsp; Pass rate: <strong>{passCount}/{graded.length}</strong></>}
        </Alert>
      )}

      {/* Scoresheet table */}
      {loading ? (
        <Box sx={{ textAlign:'center', py:4 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="warning">No students registered for this course in the selected period.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2, mb:2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                  {['#','Student No.','Student Name','CA (40%)','Exam (60%)','Total','Grade','Points','Status'].map(h => (
                    <TableCell key={h} sx={{ color:'white', fontWeight:700, fontSize:12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const g = calcGrade(row.ca, row.exam);
                  const hasMarks = row.ca !== '' || row.exam !== '';
                  return (
                    <TableRow key={idx} hover sx={{ bgcolor: hasMarks && g.points === 0 ? '#fff5f5' : 'inherit' }}>
                      <TableCell>{idx+1}</TableCell>
                      <TableCell><Typography fontFamily="monospace" fontSize={12}>{row.student_number||'—'}</Typography></TableCell>
                      <TableCell><Typography fontSize={13} fontWeight={500}>{row.last_name} {row.first_name}</Typography></TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={row.ca}
                          onChange={e => updateRow(idx, 'ca', e.target.value)}
                          inputProps={{ min:0, max:100, step:0.5 }}
                          sx={{ width:80 }} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={row.exam}
                          onChange={e => updateRow(idx, 'exam', e.target.value)}
                          inputProps={{ min:0, max:100, step:0.5 }}
                          sx={{ width:80 }} />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700} color={hasMarks ? (g.points === 0 ? 'error.main' : 'success.main') : 'text.disabled'}>
                          {hasMarks ? g.total : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {hasMarks && <Chip label={g.letter} size="small" color={gradeColor(g.letter)} />}
                      </TableCell>
                      <TableCell>{hasMarks ? g.points.toFixed(1) : '—'}</TableCell>
                      <TableCell>
                        {hasMarks && <Chip label={g.points > 0 ? 'Pass' : 'Fail'} size="small"
                          color={g.points > 0 ? 'success' : 'error'} />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={save} disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
              {saving ? 'Saving...' : 'Save All Grades'}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
